// Password strength assessment for the sign-up / reset-password forms.
//
// Server-side enforcement lives in apps/api/src/auth.ts
// (emailAndPassword.minPasswordLength = 12) plus the same length check in
// the staff create-account path. The browser-side check here is UX-only:
// it tells the user *why* their password isn't strong enough before they
// hit submit, instead of letting the server reject it after a round-trip.
//
// Kept dependency-free on purpose — zxcvbn pulls in ~400 KB of bundled
// frequency tables which is overkill for an invite-only B2B portal where
// password reuse is the realistic threat, not 7-character entropy
// estimation. The rule below (length + character-class variety + a tiny
// blocklist) catches "password123" / "Password1!" / "qwerty12345" while
// staying ~200 bytes.

const MIN_LENGTH = 12;

// Top picks from the SecLists "10-million-password-list-top-100" — covers
// the 5%-ish of compromised credentials that are just trivially common.
// We don't try to be comprehensive; that's what HaveIBeenPwned's k-anon
// API is for if we ever want to wire it.
const BLOCKLIST = new Set([
  "password", "password1", "password123", "passw0rd",
  "qwerty", "qwerty123", "qwertyuiop", "letmein",
  "welcome", "welcome1", "welcome123",
  "admin", "administrator",
  "iloveyou", "monkey", "dragon", "sunshine", "princess",
  "abc123", "123abc", "111111", "123456789", "1234567890",
  "btquick", "btquick123",
]);

export type PasswordAssessment = {
  ok: boolean;
  /** Inline hint to show under the password field. Empty when ok=true. */
  hint: string;
};

export function assessPassword(password: string): PasswordAssessment {
  if (password.length === 0) {
    return { ok: false, hint: "" };
  }
  if (password.length < MIN_LENGTH) {
    return {
      ok: false,
      hint: `At least ${MIN_LENGTH} characters (currently ${password.length}).`,
    };
  }
  if (BLOCKLIST.has(password.toLowerCase())) {
    return {
      ok: false,
      hint: "That password is on a well-known leaked-credential list — pick something else.",
    };
  }
  // Variety: at least 3 of {lower, upper, digit, symbol}. Forces something
  // more than "alllowercaseletters" without prescribing a specific shape.
  let classes = 0;
  if (/[a-z]/.test(password)) classes++;
  if (/[A-Z]/.test(password)) classes++;
  if (/[0-9]/.test(password)) classes++;
  if (/[^A-Za-z0-9]/.test(password)) classes++;
  if (classes < 3) {
    return {
      ok: false,
      hint: "Mix at least 3 of: lowercase, uppercase, digits, symbols.",
    };
  }
  return { ok: true, hint: "" };
}

export const PASSWORD_MIN_LENGTH = MIN_LENGTH;
